import { useMemo } from "react";

import { Box } from "@mui/system";
import {
  Button,
  Divider,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Avatar,
  Paper,
  IconButton,
} from "@mui/material";

import SuccessTick from "../../../../assets/SuccessTick.svg";
import UploadContainer from "../../../../assets/UploadContainer.svg";
import FileContainer from "../../../../assets/FileContainer.svg";
import Docs from "../../../../assets/Docs.svg";

import { Close as CloseIconNew } from "@mui/icons-material";

function formatBytes(bytes: any) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function FileProgressRow({
  file,
  progress,
  error,
}: {
  file: File;
  progress: number;
  error: any;
}) {
  const isDone = progress === 100;

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        p: "16px",
        borderRadius: "10px",
        border: "1px solid #DBE2FF",
        background: "#F8F9FD",
      }}
    >
      {/* File icon */}
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: "grey.100",
          color: "primary.main",
          width: 44,
          height: 44,
          mt: 0.25,
        }}
      >
        <img src={Docs} alt="docs" style={{ width: 24, height: 24 }} />
      </Avatar>

      {/* Name + size + progress bar */}
      <Box flex={1} minWidth={0}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            title={file?.name}
            sx={{
              mb: 0.25,
              color: "#000",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "21px" /* 150% */,
            }}
          >
            {file?.name}
          </Typography>
          {isDone ? <img src={UploadContainer} alt="upload complete" /> : null}
        </div>

        <Typography
          sx={{
            color: "#666",
            fontFamily: "Poppins",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "18px" /* 150% */,
          }}
        >
          {formatBytes(file?.size)}
        </Typography>

        {/* Progress bar */}
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "white",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                bgcolor: error ? "error.main" : isDone ? "#40B4B4" : "#40B4B4",
              },
            }}
          />
          <Typography
            variant="caption"
            color={error ? "error.main" : isDone ? "#666666" : "text.secondary"}
            sx={{ mt: 0.5, display: "block", fontSize: "11px" }}
          >
            {error
              ? `Error: ${error}`
              : isDone
                ? "Upload complete"
                : `Uploading... ${progress}%`}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default function UploadDialog(props: {
  files: { file: File; progress: number; error?: boolean }[];
  uploading: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const { uploading, isOpen, files, setIsOpen } = props ?? {};

  const handleCloseUpload = () => {
    if (uploading) return; // prevent closing mid-upload; remove if you want to allow it
    setIsOpen(false);
  };

  const allDone = useMemo(() => {
    return (
      files.length > 0 &&
      files.every(
        (f: { file: File; progress: number; error?: any }) =>
          f.progress === 100 || f.error,
      )
    );
  }, [files]);

  const hasErrors = useMemo(() => {
    return files.some((f: { file: File; progress: number; error?: any }) => f.error);
  }, [files]);

  const uploadingCount = useMemo(() => {
    return files.filter(
      (f: { file: File; progress: number; error?: boolean }) =>
        f.progress < 100 && !f.error,
    ).length;
  }, [files]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseUpload}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pt: 3,
          pb: 2,
          pr: 6,
        }}
      >
        <Avatar
          sx={{
            bgcolor: "#40B4B4",
            width: 40,
            height: 40,
          }}
        >
          <img
            src={FileContainer}
            alt="File Container"
            style={{ width: 40, height: 40 }}
          />
        </Avatar>

        <Box>
          <Typography
            sx={{ fontSize: "18px", fontWeight: 500, lineHeight: "27px" }}
          >
            Uploading Documents
          </Typography>
          <Typography
            sx={{
              color: "#666",
              fontFamily: "Poppins",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "18px" /* 150% */,
            }}
          >
            {uploading
              ? `${uploadingCount} file${uploadingCount !== 1 ? "s" : ""} uploading`
              : files.length > 0
                ? `${files.length} file${files.length !== 1 ? "s" : ""} selected`
                : "Select files to upload"}
          </Typography>
        </Box>

        <IconButton
          onClick={(_e: React.MouseEvent<HTMLButtonElement>) =>
            handleCloseUpload()
          }
          disabled={uploading}
          size="small"
          sx={{ position: "absolute", right: 16, top: 16 }}
        >
          <CloseIconNew fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* ── Content ── */}
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {/* File rows */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {files?.map(
            (
              entry: { file: File; progress: number; error?: boolean },
              i: number,
            ) => (
              <FileProgressRow
                key={`${entry?.file?.name}-${i}`}
                file={entry?.file}
                progress={entry?.progress}
                error={entry?.error}
              />
            ),
          )}
        </Box>

        {/* Action buttons */}
        {allDone && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!hasErrors && (
                <img
                  src={SuccessTick}
                  alt="success-tick"
                  style={{ width: 24, height: 24, marginRight: 8 }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: hasErrors ? "error.main" : "#40B4B4",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "21px",
                }}
              >
                {hasErrors
                  ? "Some files failed to upload. Please try again."
                  : "All files uploaded successfully!"}
              </Typography>
            </div>
            <Button
              variant="contained"
              onClick={(_e: React.MouseEvent<HTMLButtonElement>) =>
                handleCloseUpload()
              }
              disableElevation
              sx={{
                backgroundColor: hasErrors ? "error.main" : "#40B4B4",
                textTransform: "none",
                borderRadius: "10px",
              }}
            >
              {hasErrors ? "Close" : "Done"}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
